import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useRef } from "react";

interface Props {
  initialContent: string;
  onChange: (html: string) => void;
  token: string;
}

async function uploadFile(file: File, token: string): Promise<string | null> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/admin/upload", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) return null;
  const { url } = (await res.json()) as { url: string };
  return url;
}

export function RichEditor({ initialContent, onChange, token }: Props) {
  const imgInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({ placeholder: "Schreibe hier…" }),
    ],
    content: initialContent || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: "rich-editor-content" },
    },
  });

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const url = await uploadFile(file, token);
    if (url) editor.chain().focus().setImage({ src: url }).run();
    e.target.value = "";
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    const url = await uploadFile(file, token);
    if (url) {
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${url}" target="_blank" rel="noopener noreferrer">${file.name}</a>`,
        )
        .run();
    }
    e.target.value = "";
  }

  function addLink() {
    const url = window.prompt("URL eingeben:");
    if (!url || !editor) return;
    if (editor.state.selection.empty) {
      const text = window.prompt("Linktext:", url) ?? url;
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${url}">${text}</a>`)
        .run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }

  if (!editor) return null;

  const btn = (
    label: string,
    action: () => void,
    active?: boolean,
    disabled?: boolean,
    title?: string,
  ) => (
    <button
      type="button"
      className={`tb-btn${active ? " tb-btn--active" : ""}`}
      onClick={action}
      disabled={disabled}
      title={title ?? label}
    >
      {label}
    </button>
  );

  return (
    <div className="rich-editor">
      <div className="rich-toolbar">
        {btn("H1", () => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor.isActive("heading", { level: 1 }))}
        {btn("H2", () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive("heading", { level: 2 }))}
        {btn("H3", () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive("heading", { level: 3 }))}

        <span className="tb-sep" />

        <button type="button" className={`tb-btn tb-b${editor.isActive("bold") ? " tb-btn--active" : ""}`} onClick={() => editor.chain().focus().toggleBold().run()} title="Fett">B</button>
        <button type="button" className={`tb-btn tb-i${editor.isActive("italic") ? " tb-btn--active" : ""}`} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kursiv">I</button>
        <button type="button" className={`tb-btn tb-s${editor.isActive("strike") ? " tb-btn--active" : ""}`} onClick={() => editor.chain().focus().toggleStrike().run()} title="Durchgestrichen">S</button>
        {btn("</>", () => editor.chain().focus().toggleCode().run(), editor.isActive("code"), false, "Inline-Code")}

        <span className="tb-sep" />

        {btn("•", () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"), false, "Aufzählung")}
        {btn("1.", () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"), false, "Nummeriert")}
        {btn('"', () => editor.chain().focus().toggleBlockquote().run(), editor.isActive("blockquote"), false, "Zitat")}
        {btn("{ }", () => editor.chain().focus().toggleCodeBlock().run(), editor.isActive("codeBlock"), false, "Codeblock")}

        <span className="tb-sep" />

        {btn("—", () => editor.chain().focus().setHorizontalRule().run(), false, false, "Trennlinie")}

        <span className="tb-sep" />

        {btn("Link", addLink, editor.isActive("link"), false, "Link einfügen")}
        <button type="button" className="tb-btn" onClick={() => imgInputRef.current?.click()} title="Bild hochladen">Bild</button>
        <button type="button" className="tb-btn" onClick={() => fileInputRef.current?.click()} title="Datei hochladen">Datei</button>

        <span className="tb-sep" />

        {btn("↩", () => editor.chain().focus().undo().run(), false, !editor.can().undo(), "Rückgängig")}
        {btn("↪", () => editor.chain().focus().redo().run(), false, !editor.can().redo(), "Wiederholen")}
      </div>

      <EditorContent editor={editor} />

      <input
        ref={imgInputRef}
        type="file"
        accept="image/*"
        className="tb-hidden-input"
        onChange={handleImageChange}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.zip,.txt,.md,.docx,.xlsx,.csv"
        className="tb-hidden-input"
        onChange={handleFileChange}
      />
    </div>
  );
}
